import { Injectable, inject } from '@angular/core';

import { FixtureStage } from '@cds/betting-offer';
import { ScoreboardSlim } from '@cds/scoreboards/v1';
import { EnhancedFixtureViewGroupingConfiguration, EventModel } from '@frontend/sports/betting-offer/feature/model';
import { BetBuilderConfig, EventDetailsConfig } from '@frontend/sports/common/client-config-data-access';

import { DetailedGroupingFactory } from '../detailed-grouping.factory';
import { DetailedCreateFactory, EventDetailsParameters } from './detailed-create.factory';
import { DetailedDeleteFactory } from './detailed-delete.factory';
import { DetailedFixtureMarket } from './detailed-fixture-market.factory';
import { DetailedUpdateFactory } from './detailed-update.factory';
import { EventSitemapGroupingFactory } from './event-sitemap-grouping.factory';

@Injectable({ providedIn: 'root' })
export class DetailedFixtureFactory {
    private groupingFactory = inject(DetailedGroupingFactory);
    private createFactory = inject(DetailedCreateFactory);
    private updateFactory = inject(DetailedUpdateFactory);
    private deleteFactory = inject(DetailedDeleteFactory);
    private eventDetailsConfig = inject(EventDetailsConfig);
    private eventSitemapGroupingFactory = inject(EventSitemapGroupingFactory);
    betBuilderConfig = inject(BetBuilderConfig);

    create(eventParameters: EventDetailsParameters): EventModel {
        return this.createFactory.create(eventParameters);
    }

    update(model: EventModel, openForBetting: boolean, stage: FixtureStage): void {
        this.updateFactory.update(model, openForBetting, stage);
    }

    updateScoreboard(fixtureScoreboard: ScoreboardSlim, model: EventModel): void {
        this.updateFactory.updateScoreboard(fixtureScoreboard, model);
    }

    updateOptionGroup(
        event: EventModel,
        fixtureMarket: DetailedFixtureMarket,
        fixtureId: string,
        grouping: EnhancedFixtureViewGroupingConfiguration,
    ): void {
        this.updateFactory.updateOptionGroup(event, fixtureMarket, fixtureId, grouping);
        this.refreshModel(event, grouping);
    }

    deleteOptionGroup(event: EventModel, optionGroupId: number, grouping: EnhancedFixtureViewGroupingConfiguration): void {
        this.deleteFactory.deleteOptionGroup(event, optionGroupId);
        this.refreshModel(event, grouping);
    }

    private refreshModel(event: EventModel, grouping: EnhancedFixtureViewGroupingConfiguration): void {
        const betBuilderId = event.getBetBuilderIdByStage(
            this.betBuilderConfig.liveBetBuilderEnabledCompetitions || [],
            this.betBuilderConfig.liveBetBuilderEnabledSports || [],
        );

        // eslint-disable-next-line no-param-reassign
        event.optionSets = this.groupingFactory.getOptionSets(event.optionGroups, grouping, event.offerSource, betBuilderId, event.live);
        // eslint-disable-next-line no-param-reassign
        event.gamesCount = event.optionGroups.length;

        if (this.eventDetailsConfig.enableSitemapNavigation) {
            // eslint-disable-next-line no-param-reassign
            event.sitemapSets = this.eventSitemapGroupingFactory.getSitemapSet(event.optionSets, grouping, event.optionGroups);
        }
    }
}
